/*
  assets/gcitypartners-service-product-properties.js — GCP-PRODUCT-SERVICE-PROPERTIES-04
  Updated: 2026-09-10

  HTML owners:
  - snippets/gcitypartner-service-product-properties.liquid
  - snippets/littleg-mission-cart-properties.liquid
  Loader owner: snippets/buy-buttons.liquid
  Scope: existing Shopify add-to-cart forms only.

  Note:
  - Hydrates only inputs already rendered by Liquid.
  - Never creates Product, Product Handle, Product ID, contract or order-property owners.
  - An explicit result_id must resolve exactly; it never falls back to the last unrelated result.
*/
(function () {
  'use strict';

  var VERSION = 'GCP-PRODUCT-SERVICE-PROPERTIES-04';
  var FORM_SELECTOR = 'form[data-type="add-to-cart-form"]';
  var DEFAULT_TIMEZONE = 'Asia/Hong_Kong';
  var ALLOWED_LANES = Object.freeze({
    'urban-renewal': true,
    'land-natural-resources': true,
    'property-assets-investment': true,
    'infrastructure-engineering': true,
    'digital-city': true,
    'water-resilience': true,
    'green-city-service': true,
    'industry-park-development': true,
  });
  var SELECTORS = Object.freeze({
    status: '[data-gcity-service-properties-status]',
    statusText: '[data-gcity-service-properties-status-text]',
    timezone:
      '[data-gcp-localization-property="display_timezone"], [data-gcp-localization-property="display_timezone_private"]',
  });
  var STORAGE_KEYS = Object.freeze({
    history: 'littleg_offline_city_missions',
    lastResult: 'littleg.cityMission.lastResult',
    timezone: 'littleg_display_timezone',
  });

  var mountedForms = new Set();
  var stateByForm = new WeakMap();

  if (window.__GCP_PRODUCT_SERVICES_FORM_READY__ === VERSION) return;
  window.__GCP_PRODUCT_SERVICES_FORM_READY__ = VERSION;

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function clip(value, maxLength) {
    return clean(value).slice(0, maxLength || 240);
  }

  function opaque(value) {
    var text = clean(value);
    return /^[A-Za-z0-9._:-]{1,160}$/.test(text) ? text : '';
  }

  function token(value, fallback) {
    var text = clean(value)
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9:_-]/g, '')
      .slice(0, 120);
    return text || clean(fallback);
  }

  function safeJson(raw, fallback) {
    try {
      var parsed = raw ? JSON.parse(raw) : fallback;
      return parsed == null ? fallback : parsed;
    } catch (_) {
      return fallback;
    }
  }

  function asArray(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'object') {
      var items = value.items || value.results || value.history || value.cards;
      if (Array.isArray(items)) return items;
    }
    return [value];
  }

  function storageValue(storage, key) {
    if (!storage) return '';
    try {
      return storage.getItem(key) || '';
    } catch (_) {
      return '';
    }
  }

  function storageValues(key) {
    var localValue = storageValue(window.localStorage, key);
    var sessionValue = storageValue(window.sessionStorage, key);
    var output = [];
    if (localValue) output.push(localValue);
    if (sessionValue && sessionValue !== localValue) output.push(sessionValue);
    return output;
  }

  function readTextStorage(key, fallback) {
    var values = storageValues(key);
    return values.length ? values[0] : fallback;
  }

  function readParams() {
    try {
      return new URLSearchParams(window.location.search || '');
    } catch (_) {
      return new URLSearchParams();
    }
  }

  function entryId(entry) {
    return entry && typeof entry === 'object'
      ? opaque(entry.result_id || entry.record_id || entry.card_id || entry.cardId || entry.id)
      : '';
  }

  function findStoredResult(id) {
    var expected = opaque(id);
    if (!expected) return null;

    var values = storageValues(STORAGE_KEYS.history);
    for (var valueIndex = 0; valueIndex < values.length; valueIndex += 1) {
      var rows = asArray(safeJson(values[valueIndex], []));
      for (var rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
        if (entryId(rows[rowIndex]) === expected) return rows[rowIndex];
      }
    }
    return null;
  }

  function exactResult(id) {
    var expected = opaque(id);
    if (!expected) return null;

    var owner = window.LittleGResultStorage;
    if (owner && typeof owner.getResult === 'function') {
      try {
        var result = owner.getResult(expected);
        if (result && entryId(result) === expected) return result;
      } catch (_) {}
    }

    return findStoredResult(expected);
  }

  function lastResult() {
    var owner = window.LittleGResultStorage;
    if (owner && typeof owner.getLastResult === 'function') {
      try {
        var res = owner.getLastResult();
        if (res) return res;
      } catch (_) {}
    }

    var values = storageValues(STORAGE_KEYS.lastResult);
    return values.length ? safeJson(values[0], null) : null;
  }

  function resolvePayload(requestedId) {
    var explicitId = opaque(requestedId);
    if (explicitId) {
      var exact = exactResult(explicitId);
      return {
        requested: true,
        requestedId: explicitId,
        found: Boolean(exact),
        payload: exact || {},
      };
    }

    var latest = lastResult();
    return {
      requested: false,
      requestedId: '',
      found: Boolean(latest),
      payload: latest || {},
    };
  }

  function valueFrom(payload, params, keys, fallback) {
    for (var index = 0; index < keys.length; index += 1) {
      var key = keys[index];
      var paramValue = params.get(key);
      if (clean(paramValue)) return paramValue;
      if (payload && clean(payload[key])) return payload[key];
    }
    return fallback || '';
  }

  function canonicalLane(value) {
    var candidate = token(Array.isArray(value) ? value[0] : value, '');
    if (!candidate || candidate === 'review-brief') return candidate;

    var owner = window.GCityPartnersServiceLaneMap;
    if (owner && typeof owner.canonical === 'function') {
      try {
        var resolved = token(owner.canonical(candidate), '');
        if (ALLOWED_LANES[resolved]) return resolved;
      } catch (_) {}
    }

    return ALLOWED_LANES[candidate] ? candidate : '';
  }

  function firstInputValue(form, selectors) {
    var input = form.querySelector(selectors);
    return input ? clean(input.value) : '';
  }

  function resolveService(form, payload, params) {
    var requested =
      params.get('service_lane') ||
      params.get('service') ||
      params.get('lane') ||
      params.get('scenario') ||
      params.get('scene') ||
      payload.service_lane ||
      '';

    if (!requested && Array.isArray(payload.service_lane_suggestion)) {
      requested = payload.service_lane_suggestion[0] || '';
    }

    var existing = firstInputValue(
      form,
      '[data-gcp-property="service_lane"], [data-property="service_lane"]'
    );
    var explicitRequested = Boolean(
      clean(
        params.get('service_lane') ||
          params.get('service') ||
          params.get('lane') ||
          params.get('scenario') ||
          params.get('scene')
      )
    );
    var canonicalRequested = canonicalLane(requested);
    var canonicalExisting = canonicalLane(existing);

    return {
      explicit: explicitRequested,
      invalid: explicitRequested && !canonicalRequested,
      value: canonicalRequested || canonicalExisting || 'review-brief',
    };
  }

  function validTimezone(value) {
    var timezone = clip(value, 100);
    if (!timezone) return '';
    try {
      new Intl.DateTimeFormat('en', { timeZone: timezone }).format(new Date());
      return timezone;
    } catch (_) {
      return '';
    }
  }

  function resolveTimezone(value) {
    return (
      validTimezone(value) ||
      validTimezone(readTextStorage(STORAGE_KEYS.timezone, '')) ||
      DEFAULT_TIMEZONE
    );
  }

  function rememberInput(state, input) {
    if (!state.originalValues.has(input)) {
      state.originalValues.set(input, input.value);
    }
  }

  function setInputs(form, state, selector, value) {
    var text = clean(value);
    if (!text) return 0;

    var count = 0;
    form.querySelectorAll(selector).forEach(function (input) {
      rememberInput(state, input);
      input.value = text;
      count += 1;
    });
    return count;
  }

  function restoreInputs(state) {
    state.originalValues.forEach(function (value, input) {
      if (input && input.isConnected) input.value = value;
    });
    state.originalValues.clear();
  }

  function applyTimezone(form, state, value) {
    return setInputs(form, state, SELECTORS.timezone, resolveTimezone(value));
  }

  function setStatus(form, state, message) {
    const status = form.querySelector(SELECTORS.status);
    if (!status) return;

    const text = status.querySelector(SELECTORS.statusText);
    const purchaseOwner = form.closest('product-form');
    const isGTenderSubscription = purchaseOwner?.dataset.productId === '15645008920657';
    const hideRoutineDiagnostic = isGTenderSubscription && state === 'service-only';

    status.dataset.state = state;
    status.hidden = hideRoutineDiagnostic;

    if (text) {
      text.textContent = hideRoutineDiagnostic ? '' : message;
    }
  }

  function hydrateForm(form, options) {
    if (!form) return null;

    var state = stateByForm.get(form);
    if (!state) return null;
    if (!options || options.restore !== false) restoreInputs(state);

    var params = readParams();
    var requestedResultId = opaque(params.get('result_id') || params.get('result') || '');
    var resolution = resolvePayload(requestedResultId);
    var payload = resolution.payload;
    var service = resolveService(form, payload, params);
    var source = token(valueFrom(payload, params, ['source'], 'gcitypartners'), 'gcitypartners');
    var scenario = token(valueFrom(payload, params, ['scenario', 'scenario_id'], ''), '');
    var zone = token(valueFrom(payload, params, ['zone', 'zone_id'], ''), '');
    var missionId = opaque(valueFrom(payload, params, ['mission_id'], ''));
    var finalResultId = resolution.found ? opaque(entryId(payload) || requestedResultId) : '';

    var resultInputCount = setInputs(
      form,
      state,
      '[data-mission-property="result_id"], [data-gcp-property="result_id"], [data-property="mission_result_id"]',
      finalResultId
    );
    setInputs(
      form,
      state,
      '[data-mission-property="source"], [data-gcp-property="source"], [data-property="mission_source"]',
      source
    );
    setInputs(
      form,
      state,
      '[data-mission-property="scenario_id"], [data-gcp-property="scenario_id"], [data-property="scenario_id"]',
      scenario
    );
    setInputs(form, state, '[data-mission-property="zone_id"], [data-property="zone_id"]', zone);
    setInputs(
      form,
      state,
      '[data-mission-property="mission_id"], [data-property="mission_id"]',
      missionId
    );

    if (!service.invalid) {
      setInputs(
        form,
        state,
        '[data-mission-property="service_lane"], [data-gcp-property="service_lane"], [data-property="service_lane"]',
        service.value
      );
    }

    setInputs(
      form,
      state,
      '[data-gcp-property="professional_scope"]',
      'Preliminary professional service intake linked to LittleG / GCityPartners Review Brief.'
    );
    applyTimezone(form, state, options && options.timezone);

    var contextState = 'service-only';
    var message = 'Professional service context attached. No Mission result is linked.';

    if (service.invalid) {
      contextState = 'invalid-service-lane';
      message =
        'The requested service lane is not recognized. Existing product contract values were preserved.';
    } else if (resolution.requested && !resolution.found) {
      contextState = 'missing-result';
      message =
        'The requested Mission result was not found. No fallback result was attached to this order.';
    } else if (resolution.found && finalResultId && resultInputCount > 0) {
      contextState = 'linked';
      message = 'Mission and Review Brief context linked to this order.';
    } else if (resolution.found && finalResultId && resultInputCount === 0) {
      contextState = 'input-missing';
      message = 'Mission context was found, but no rendered Result ID property accepts it.';
    } else if (scenario || zone || missionId) {
      contextState = 'context-only';
      message = 'Scenario context linked without a Mission result.';
    }

    setStatus(form, contextState, message);
    form.dataset.gcpPropertiesHydrated = VERSION;
    form.dataset.gcpPropertiesOwner = 'assets/gcitypartners-service-product-properties.js';
    form.dataset.gcpPropertiesContextState = contextState;
    form.dataset.gcpPropertiesRequestedResult = requestedResultId;
    state.lastContextState = contextState;

    return {
      state: contextState,
      requested_result_id: requestedResultId,
      linked_result_id: finalResultId,
      service_lane: service.invalid ? '' : service.value,
    };
  }

  function mount(form) {
    if (!form || stateByForm.has(form)) return;

    var state = {
      originalValues: new Map(),
      lastContextState: '',
    };
    stateByForm.set(form, state);
    mountedForms.add(form);
    hydrateForm(form, { restore: false });
  }

  function destroy(form) {
    var state = form ? stateByForm.get(form) : null;
    if (!state) return;

    restoreInputs(state);
    mountedForms.delete(form);
    stateByForm.delete(form);
    form.removeAttribute('data-gcp-properties-hydrated');
    form.removeAttribute('data-gcp-properties-owner');
    form.removeAttribute('data-gcp-properties-context-state');
    form.removeAttribute('data-gcp-properties-requested-result');
  }

  /** @param {HTMLElement} root */
  function init(root) {
    var scope = root || document;
    if (scope.matches && scope.matches(FORM_SELECTOR)) mount(scope);
    if (!scope.querySelectorAll) return;
    scope.querySelectorAll(FORM_SELECTOR).forEach(mount);
  }

  function destroyAll(root) {
    var scope = root || document;
    if (scope.matches && scope.matches(FORM_SELECTOR)) destroy(scope);
    if (!scope.querySelectorAll) return;
    scope.querySelectorAll(FORM_SELECTOR).forEach(destroy);
  }

  function refreshAll(options) {
    mountedForms.forEach(function (form) {
      if (!form.isConnected) {
        destroy(form);
        return;
      }
      hydrateForm(form, options || {});
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    destroyAll(event.target || document);
  });

  window.addEventListener('littleg:timezone-updated', function (event) {
    refreshAll({
      timezone: event && event.detail ? event.detail.timezone : '',
    });
  });
  window.addEventListener('pageshow', function () {
    refreshAll();
  });
  window.addEventListener('popstate', function () {
    refreshAll();
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        init(document);
      },
      { once: true }
    );
  } else {
    init(document);
  }

  // 向下相容 LittleGMissionCartProperties 介面，直接委派給主執行期
  window.LittleGMissionCartProperties = Object.freeze({
    populate: function () {
      refreshAll();
    },
  });

  window.GCityPartnersServiceProductProperties = Object.freeze({
    version: VERSION,
    init: init,
    destroy: destroyAll,
    refresh: refreshAll,
    hydrateForm: function (form) {
      if (!stateByForm.has(form)) mount(form);
      return hydrateForm(form);
    },
    applyTimezone: function (root, value) {
      var scope = root || document;
      var forms = [];
      if (scope.matches && scope.matches(FORM_SELECTOR)) forms.push(scope);
      if (scope.querySelectorAll) {
        Array.prototype.push.apply(forms, scope.querySelectorAll(FORM_SELECTOR));
      }
      forms.forEach(function (form) {
        if (!stateByForm.has(form)) mount(form);
        var state = stateByForm.get(form);
        applyTimezone(form, state, value);
      });
    },
  });
})();
